import React, { FC, useEffect, useState } from 'react'
import { Box, Text, chakra, HStack, useRadioGroup } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { RadioButtonProps } from '../radio-buttons/interfaces'
import { selectAnswers, selectCurrentAnswers, selectScenarioIsWaitingForAnswer, selectScenarioType } from '../../state'
import { ScenarioType, Steps } from '../../state/interfaces'
import { useSelector } from 'react-redux'
import { getCookie } from '../../scenarios'
import { LoadingNewAnswer } from '../spinners/loading-new-answer'

interface Choice {
  value: string
  label: string
  image?: string
  isChecked?: boolean
}

export interface RadioButtonGroupProps {
  name: string
  choices: Choice[]
  value?: string
  onChange?: (values: string[]) => void
  component: FC<RadioButtonProps>
}

export const providerAvailability: FC<RadioButtonGroupProps> = ({
  choices,
  value,
  onChange,
  name,
  component: Component,
}) => {
  const currentAnswers = useSelector(selectCurrentAnswers)
  const scenarioIsWaitingForAnswer = useSelector(selectScenarioIsWaitingForAnswer)
  const isDeal =
    currentAnswers[0] === 'continue-with-provider' ? currentAnswers[1] || 'false' : currentAnswers[0] || 'false'
  const selectedValue = currentAnswers[0] === 'continue-with-provider' ? undefined : value

  const { getRadioProps, getRootProps } = useRadioGroup({
    onChange: (value) => onChange && onChange([value, isDeal]),
    value: selectedValue,
    name,
  })

  const [providerAvailable, setProviderAvailable] = useState(false)
  const answers = useSelector(selectAnswers)
  const scenarioType: ScenarioType = useSelector(selectScenarioType) as ScenarioType
  const rawAddress = answers['address-location']?.[0]
  const streetLine = [answers['address-location']?.[1], answers['address-location']?.[2]].filter(Boolean).join(' ')
  const localityLine = [
    answers['address-location']?.[3],
    [answers['address-location']?.[4], answers['address-location']?.[6]].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ')
  const countryLine = answers['address-location']?.[5]
  const hasStructuredAddress = Boolean(streetLine || localityLine || countryLine)

  useEffect(() => {
    if (answers[Steps.PROVIDER_AVAILABILITY] && answers[Steps.PROVIDER_AVAILABILITY]![0] === 'true') {
      setProviderAvailable(true)
    } else {
      setProviderAvailable(false)
    }
  }, [answers[Steps.PROVIDER_AVAILABILITY]])

  const { t } = useTranslation()
  const providerName: string | undefined = getCookie('providerName')

  return (
    <Box>
      {providerAvailable ? (
        scenarioType === ScenarioType.LEAD_INTERNET ? (
          <Box textAlign={'center'}>
            <Text fontSize="xs" fontWeight="bold" color="green.500" textTransform="uppercase" marginBottom={2}>
              {t('success')}
            </Text>
            <Text fontSize="md" marginBottom={2}>
              {t('provider-available-at', { providerName })}
            </Text>
            {hasStructuredAddress ? (
              <>
                <Text fontSize="3xl" fontWeight="bold" marginBottom={2} color="green.500" textTransform="capitalize">
                  {streetLine}
                </Text>
                <Text fontSize="xl" fontWeight="bold" marginBottom={2} color="green.500" textTransform="capitalize">
                  <chakra.span textTransform="none">{localityLine}</chakra.span>
                </Text>
                <Text fontSize="xl" fontWeight="bold" marginBottom={2} color="green.500" textTransform="capitalize">
                  {countryLine}
                </Text>
              </>
            ) : (
              <Text fontSize="xl" fontWeight="bold" marginBottom={2} color="green.500">
                {rawAddress}
              </Text>
            )}
          </Box>
        ) : (
          <Box textAlign={'center'}>
            <Text fontSize="xs" fontWeight="bold" color="green.500" textTransform="uppercase" marginBottom={2}>
              {t('success')}
            </Text>
            <Text fontSize="md" marginBottom={2}>
              {t('plan-available-at-mobile', { providerName })}
            </Text>
          </Box>
        )
      ) : scenarioType === ScenarioType.LEAD_INTERNET ? (
        <Box textAlign={'center'}>
          <Text fontSize="xs" fontWeight="bold" color="error" textTransform="uppercase" marginBottom={2}>
            {t('sorry')}
          </Text>
          <Text fontSize="md" marginBottom={2}>
            {providerName === 'Bell'
              ? t('plan-not-available-at', { providerName })
              : t('provider-not-available-at', { providerName })}
          </Text>
          {hasStructuredAddress ? (
            <>
              <Text fontSize="3xl" fontWeight="bold" marginBottom={2} color="error" textTransform="capitalize">
                {streetLine}
              </Text>
              <Text fontSize="xl" fontWeight="bold" marginBottom={2} color="error" textTransform="capitalize">
                <chakra.span textTransform="none">{localityLine}</chakra.span>
              </Text>
              <Text fontSize="xl" fontWeight="bold" marginBottom={2} color="error" textTransform="capitalize">
                {countryLine}
              </Text>
            </>
          ) : (
            <Text fontSize="xl" fontWeight="bold" marginBottom={2} color="error">
              {rawAddress}
            </Text>
          )}
          <Text color="darkGrey" fontSize="xs" marginBottom={9} as="i">
            {providerName === 'Bell' ? t('redirect-to-results') : t('let-us-notify-you')}
          </Text>
        </Box>
      ) : (
        <Box textAlign={'center'}>
          <Text fontSize="xs" fontWeight="bold" color="error" textTransform="uppercase" marginBottom={2}>
            {t('sorry')}
          </Text>
          <Text fontSize="md" marginBottom={2}>
            {t('plan-not-available-at-mobile', { providerName })}
          </Text>
          <Text color="darkGrey" fontSize="xs" marginBottom={9} as="i">
            {t('redirect-to-results')}
          </Text>
        </Box>
      )}

      <HStack {...getRootProps()} justifyContent="center" margin="2vh">
        {scenarioIsWaitingForAnswer ? (
          <LoadingNewAnswer />
        ) : (
          choices.map((props) => (
            <Component key={`choice-${props.value}`} {...getRadioProps({ value: props.value })} {...props} />
          ))
        )}
      </HStack>
    </Box>
  )
}
